import { useEffect, useRef, useState, type ReactNode } from "react";

const TRIGGER = 80;
const MAX = 130;
const SNAP = 64;
const REFRESH_MS = 1400;

export function PullToRefresh({ children }: { children: ReactNode }) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const active = useRef(false);
  const pullRef = useRef(0);

  useEffect(() => {
    pullRef.current = pull;
  }, [pull]);

  useEffect(() => {
    function onStart(e: TouchEvent) {
      if (window.scrollY > 0 || refreshing) return;
      startY.current = e.touches[0]?.clientY ?? null;
      active.current = true;
    }
    function onMove(e: TouchEvent) {
      if (!active.current || startY.current === null) return;
      if (window.scrollY > 0) {
        active.current = false;
        setPull(0);
        return;
      }
      const dy = (e.touches[0]?.clientY ?? 0) - startY.current;
      if (dy > 0) {
        const eased = Math.min(MAX, dy * 0.55);
        setPull(eased);
      } else {
        setPull(0);
      }
    }
    async function onEnd() {
      if (!active.current) return;
      active.current = false;
      startY.current = null;
      if (pullRef.current >= TRIGGER) {
        setRefreshing(true);
        setPull(SNAP);
        await new Promise((r) => setTimeout(r, REFRESH_MS));
        setRefreshing(false);
        setPull(0);
      } else {
        setPull(0);
      }
    }
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd);
    window.addEventListener("touchcancel", onEnd);
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
    };
  }, [refreshing]);

  const progress = Math.min(1, pull / TRIGGER);
  const animated = refreshing || !active.current;

  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none fixed inset-x-0 top-[env(safe-area-inset-top)] z-40 flex justify-center overflow-hidden"
        style={{
          height: pull,
          transition: animated
            ? "height 240ms cubic-bezier(0.32, 0.72, 0, 1)"
            : "none",
        }}
      >
        <div
          className="flex items-end justify-center pb-3"
          style={{
            opacity: refreshing ? 1 : Math.max(0, (progress - 0.3) / 0.7),
            transform: `scale(${refreshing ? 1 : Math.max(0.6, progress)})`,
            transformOrigin: "bottom center",
            transition: animated
              ? "opacity 200ms ease, transform 200ms ease"
              : "none",
          }}
        >
          <div className="inline-flex size-10 items-center justify-center rounded-full bg-[var(--surface-1)]/90 shadow-[0_4px_14px_rgba(15,23,42,0.12)] ring-1 ring-[var(--border-1)] backdrop-blur">
            <IOSSpinner spinning={refreshing} progress={progress} />
          </div>
        </div>
      </div>
      <div
        style={{
          transform: `translate3d(0, ${pull}px, 0)`,
          transition: animated
            ? "transform 280ms cubic-bezier(0.32, 0.72, 0, 1)"
            : "none",
        }}
      >
        {children}
      </div>
    </>
  );
}

const SPOKES = [
  { x1: 1200, y1: 600, x2: 1200, y2: 100, opacity: 1 },
  { x1: 1200, y1: 2300, x2: 1200, y2: 1800, opacity: 0.5 },
  { x1: 900, y1: 680.4, x2: 650, y2: 247.4, opacity: 0.917 },
  { x1: 1750, y1: 2152.6, x2: 1500, y2: 1719.6, opacity: 0.417 },
  { x1: 680.4, y1: 900, x2: 247.4, y2: 650, opacity: 0.833 },
  { x1: 2152.6, y1: 1750, x2: 1719.6, y2: 1500, opacity: 0.333 },
  { x1: 600, y1: 1200, x2: 100, y2: 1200, opacity: 0.75 },
  { x1: 2300, y1: 1200, x2: 1800, y2: 1200, opacity: 0.25 },
  { x1: 680.4, y1: 1500, x2: 247.4, y2: 1750, opacity: 0.667 },
  { x1: 2152.6, y1: 650, x2: 1719.6, y2: 900, opacity: 0.167 },
  { x1: 900, y1: 1719.6, x2: 650, y2: 2152.6, opacity: 0.583 },
  { x1: 1750, y1: 247.4, x2: 1500, y2: 680.4, opacity: 0.083 },
];

function IOSSpinner({
  spinning,
  progress,
}: {
  spinning: boolean;
  progress: number;
}) {
  const eased = Math.max(0, Math.min(1, progress));
  return (
    <svg
      width={22}
      height={22}
      viewBox="0 0 2400 2400"
      role="img"
      aria-label={
        spinning ? "Rafraîchissement en cours" : "Tirez pour rafraîchir"
      }
      className="text-[#6E6E73]"
    >
      <g
        stroke="currentColor"
        strokeWidth="220"
        strokeLinecap="round"
        fill="none"
      >
        {SPOKES.map((s, i) => (
          <line
            key={i}
            x1={s.x1}
            y1={s.y1}
            x2={s.x2}
            y2={s.y2}
            opacity={spinning ? s.opacity : Math.max(0.05, eased * s.opacity)}
          />
        ))}
        {spinning ? (
          <animateTransform
            attributeName="transform"
            attributeType="XML"
            type="rotate"
            keyTimes="0;0.08333;0.16667;0.25;0.33333;0.41667;0.5;0.58333;0.66667;0.75;0.83333;0.91667"
            values="0 1199 1199;30 1199 1199;60 1199 1199;90 1199 1199;120 1199 1199;150 1199 1199;180 1199 1199;210 1199 1199;240 1199 1199;270 1199 1199;300 1199 1199;330 1199 1199"
            dur="0.83333s"
            begin="0s"
            repeatCount="indefinite"
            calcMode="discrete"
          />
        ) : null}
      </g>
    </svg>
  );
}
